import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, Subscription } from "rxjs";

const LANG_QUERY = "lang";
const LANG_FR = "fr";
const SESSION_GUESS_KEY = "portfolioLangGuessDone";

@Injectable({ providedIn: "root" })
export class LanguageService implements OnDestroy {
    private sub?: Subscription;

    constructor(
        private readonly router: Router,
        private readonly translate: TranslateService,
    ) {
        this.translate.addLangs(["en", LANG_FR]);
        this.translate.setFallbackLang("en");

        this.sub = this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                // Router URL strings often omit query params on fragment-only / anchor
                // navigations; the browser location still has ?lang=fr.
                this.applyLangFromBrowserLocation();
            });

        this.applyLangFromBrowserLocation();
        this.tryGuessBrowserLang();
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    /** French uses `?lang=fr`; English leaves the param unset. */
    setLang(lang: "en" | typeof LANG_FR): void {
        this.router.navigate([], {
            queryParams: { [LANG_QUERY]: lang === LANG_FR ? LANG_FR : null },
            queryParamsHandling: "merge",
            replaceUrl: true,
        });
    }

    isFrenchUrl(url: string): boolean {
        try {
            const q = new URL(url, "http://local").searchParams.get(LANG_QUERY);
            return q === LANG_FR;
        } catch {
            return false;
        }
    }

    /** Prefer `window.location` so hash links (#projects, etc.) keep `?lang=fr`. */
    private applyLangFromBrowserLocation(): void {
        let lang: "en" | typeof LANG_FR = "en";
        if (typeof window !== "undefined") {
            try {
                const q = new URL(window.location.href).searchParams.get(
                    LANG_QUERY,
                );
                if (q === LANG_FR) {
                    lang = LANG_FR;
                }
            } catch {
                lang = this.isFrenchUrl(this.router.url) ? LANG_FR : "en";
            }
        } else {
            lang = this.isFrenchUrl(this.router.url) ? LANG_FR : "en";
        }

        this.translate.use(lang);
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang;
        }
    }

    /**
     * If there is no `lang` query yet, and the browser prefers French, add `?lang=fr` once per session.
     */
    private tryGuessBrowserLang(): void {
        if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
            return;
        }
        if (sessionStorage.getItem(SESSION_GUESS_KEY) === "1") {
            return;
        }
        try {
            const sp = new URL(window.location.href).searchParams;
            if (sp.has(LANG_QUERY)) {
                sessionStorage.setItem(SESSION_GUESS_KEY, "1");
                return;
            }
        } catch {
            return;
        }

        const prefersFr = this.browserPrefersFrench();
        sessionStorage.setItem(SESSION_GUESS_KEY, "1");
        if (!prefersFr) {
            return;
        }

        this.router.navigate([], {
            queryParams: { [LANG_QUERY]: LANG_FR },
            queryParamsHandling: "merge",
            replaceUrl: true,
        });
    }

    private browserPrefersFrench(): boolean {
        if (typeof navigator === "undefined") {
            return false;
        }
        const langs = navigator.languages?.length
            ? navigator.languages
            : [navigator.language];
        return langs.some((l) => l.toLowerCase().startsWith("fr"));
    }
}
